import { RotateCcw } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { BudgetAccountCodeModal } from '../components/BudgetAccountCodeModal';
import { budgetAccountCodesApi } from '../budgetCatalogsApi';
import type { BudgetAccountCodeListItem } from '../budgetCatalogTypes';

/**
 * The chart of accounts, reached through **Procurement → Investment Planning → Settings**.
 *
 * The anchor is the CODE. This is the case where the code IS the identity: it is unique among
 * the live rows, it is what a budget line is charged against, and two accounts legitimately
 * share a description. The description keeps its own column beside it.
 */
export function BudgetAccountCodesIndexPage() {
  const navigate = useNavigate();

  const [editing, setEditing] = useState<BudgetAccountCodeListItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    account: BudgetAccountCodeListItem;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchAccounts = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      budgetAccountCodesApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (account: BudgetAccountCodeListItem): MenuItem[] => [
      {
        key: `edit-${account.id}`,
        label: 'Edit',
        permission: 'budget-account-codes.update',
        onClick: () => setEditing(account),
      },
      {
        key: `restore-${account.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'budget-account-codes.delete',
        disabledReason: account.isActive ? 'This account is already active.' : null,
        onClick: () => setConfirming({ kind: 'activate', account }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') {
        await budgetAccountCodesApi.deactivate(confirming.account.id);
      } else {
        await budgetAccountCodesApi.activate(confirming.account.id);
      }

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0),
      );
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<BudgetAccountCodeListItem>
        module="budget-account-codes"
        breadcrumb={[
          'Procurement',
          'Investment Planning',
          { label: 'Settings', to: '/procurement/investment-planning/settings' },
          'Account Codes',
        ]}
        columns={COLUMNS}
        facets={[{ key: 'status', label: 'Status' }]}
        auditEntityTypes={['BudgetAccountCode']}
        rowKey={(account) => account.id}
        fetch={fetchAccounts}
        reloadKey={reloadKey}
        searchPlaceholder="Search account codes"
        emptyMessage="No account codes yet. Add the expenditure accounts a budget line can be charged against."
        onCreate={() => setCreating(true)}
        createPermission="budget-account-codes.create"
        onRowOpen={(account) =>
          navigate(`/procurement/investment-planning/settings/account-codes/${account.id}`)
        }
        openPermission="budget-account-codes.read"
        rowActions={rowActions}
        onDelete={(account) => setConfirming({ kind: 'deactivate', account })}
        deletePermission="budget-account-codes.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(account) =>
          account.isActive ? null : 'This account is already deleted.'
        }
        onClearData={() => budgetAccountCodesApi.clear()}
        clearDataPermission="budget-account-codes.clear-data"
        otherInfo={(account) => account}
      />

      {creating || editing !== null ? (
        <BudgetAccountCodeModal
          open
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'deactivate' ? 'Delete account code' : 'Restore account code'}
          danger={confirming.kind === 'deactivate'}
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`budget-account-code-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `"${confirming.account.code}" stops being offered when a budget line is charged.`
                  : `"${confirming.account.code}" is offered again when a budget line is charged.`}
              </p>

              {confirming.kind === 'deactivate' ? (
                <p>
                  Budget lines already charged to it keep resolving it. Its code is also freed
                  for a new account to take — so a restore can be refused if it is taken
                  meanwhile.
                </p>
              ) : null}

              <p>Nothing is erased — a deleted account can be restored from this list.</p>

              {error ? (
                <p
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700"
                >
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<BudgetAccountCodeListItem>[] = [
  {
    key: 'code',
    header: 'Code',
    width: 170,
    sortable: true,
    render: (account) => <TextCell value={account.code} mono />,
  },
  {
    key: 'description',
    header: 'Description',
    width: 340,
    flex: true,
    sortable: true,
    summary: true,
    render: (account) => <TextCell value={account.description} />,
  },
  {
    key: 'acctNo',
    header: 'Account no.',
    width: 140,
    sortable: true,
    summary: true,
    // `mono`, never `numeric`: an account number is an identifier, so "0100" must keep its
    // leading zero at full strength rather than dimming as a quantity would.
    render: (account) => <TextCell value={account.acctNo} mono />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (account) =>
      account.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Deleted</StatusText>
      ),
  },
];
